import { Skeleton } from '@mui/material';
import { type JSX, useMemo, useState } from 'react';

import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { RiskCallout } from 'Components/RiskRegister/RiskCallout/RiskCallout';
import { Text } from 'Components/Text/Text';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { RISK_MANAGEMENT_CONTROL_EFFECTIVENESS, RISK_MANAGEMENT_TARGET_RESIDUAL_RISK } from 'Config/Tooltips';
import { ALL_RISKS, RiskCategoryResponse, RiskResponse } from 'Models/RiskRegister';

import styles from './RiskRegisterDashboard.module.css';

export interface RiskCalloutsProps {
    error?: Error;
    overallControlEffectiveness?: number;
    riskCategories?: RiskCategoryResponse[];
    risks?: RiskResponse[];
}

/**
 * This is a wrapper for multiple RiskCallout components.
 * Normally, multiple RiskCallout components are passed as an array to the "dashlets" prop since they are used on pages where:
 *   1) a Risk Category drop-down menu affects the entire page (i.e., the Risk Listing page); or
 *   2) the page is specific to a Risk Category (i.e., the Risk Review Details page).
 * In the case of dashboard pages, all of the dashlets operate independently, so there is no way to have a single Risk Category drop-down menu that affects all of the RiskCallout components (since they are individual dashlets).
 * Therefore, this wrapper component is used to group multiple RiskCallout components together in a single dashlet that can be affected by a Risk Category drop-down menu.
 */
export const RiskCallouts = (props: RiskCalloutsProps): JSX.Element => {
    const [selectedRiskCategory, setSelectedRiskCategory] = useState<string>(ALL_RISKS); // Filters Risks by category. Updated when the drop-down selector changes.

    /** The Risks to include in the RiskCallout components. */
    const selectedRisks: RiskResponse[] | undefined = useMemo(() => props.risks?.filter((risk) => selectedRiskCategory === ALL_RISKS || selectedRiskCategory === risk.category.id), [props.risks, selectedRiskCategory]);

    if (props.error) {
        return (
            <Text variant="Header4" color="lightGray">
                {props.error.message}
            </Text>
        );
    } else if (props.overallControlEffectiveness === undefined || props.riskCategories === undefined || selectedRisks === undefined) {
        return <Skeleton variant="rounded" height={136} width={'100%'} />;
    } else {
        return (
            <>
                <div className={styles.dashletHeaderWithoutTitleWithFilter}>
                    <div></div>
                    <FormFieldSelectDark formFieldId="selectedRiskCategory" formFieldLabel="Risk Category" handleChange={(value: ChangeEventType) => setSelectedRiskCategory(value as string)} options={props.riskCategories.map((riskCategory) => ({ label: riskCategory.title, value: riskCategory.id }))} selectedOption={selectedRiskCategory} />
                </div>
                <div className={styles.riskCalloutsContainer}>
                    <div className={styles.riskCallout}>
                        <Text variant="Header2" color="white">
                            Inherent Risk
                        </Text>
                        <RiskCallout calloutType="total_inherent_risk" completeEnvironmentEffectivenessAverage={props.overallControlEffectiveness} risks={selectedRisks} />
                    </div>
                    <div className={styles.riskCallout}>
                        <div className={styles.riskCalloutTitle}>
                            <Text variant="Header2" color="white">
                                Control Effectiveness
                            </Text>
                            <FormFieldTooltip text={RISK_MANAGEMENT_CONTROL_EFFECTIVENESS} />
                        </div>
                        <RiskCallout calloutType="control_environment_effectiveness" completeEnvironmentEffectivenessAverage={props.overallControlEffectiveness} risks={selectedRisks} />
                    </div>
                    <div className={styles.riskCallout}>
                        <Text variant="Header2" color="white">
                            Current Residual Risk
                        </Text>
                        <RiskCallout calloutType="total_current_risk" completeEnvironmentEffectivenessAverage={props.overallControlEffectiveness} risks={selectedRisks} />
                    </div>
                    <div className={styles.riskCallout}>
                        <div className={styles.riskCalloutTitle}>
                            <Text variant="Header2" color="white">
                                Target Residual Risk
                            </Text>
                            <FormFieldTooltip text={RISK_MANAGEMENT_TARGET_RESIDUAL_RISK} />
                        </div>
                        <RiskCallout calloutType="total_target_risk" completeEnvironmentEffectivenessAverage={props.overallControlEffectiveness} risks={selectedRisks} />
                    </div>
                </div>
            </>
        );
    }
};
